import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  User, 
  Mail, 
  Building2, 
  Shield,
  LogOut,
  Bell,
  BellOff,
  Loader2,
  GraduationCap,
  Users
} from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    inapp_notifications: true,
    student_email_notifications: true
  });
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    fetchNotificationPreferences();
  }, []);

  const fetchNotificationPreferences = async () => {
    try {
      const response = await api.get("/auth/me/notification-preferences");
      setNotificationPrefs({
        email_notifications: response.data.email_notifications ?? true,
        inapp_notifications: response.data.inapp_notifications ?? true,
        student_email_notifications: response.data.student_email_notifications ?? true
      });
    } catch (error) {
      console.error("Failed to load notification preferences:", error);
    } finally {
      setLoadingPrefs(false);
    }
  };

  const updateNotificationPreference = async (key, value) => {
    setSavingPrefs(true);
    try {
      const response = await api.put("/auth/me/notification-preferences", {
        [key]: value
      });
      setNotificationPrefs(prev => ({
        ...prev,
        [key]: response.data[key] ?? value
      }));
      toast.success("Notification preferences updated");
    } catch (error) {
      toast.error("Failed to update preferences");
      // Revert on error
      setNotificationPrefs(prev => ({ ...prev, [key]: !value }));
    } finally {
      setSavingPrefs(false);
    }
  };

  // Helper to get user's display name
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) return `${user.first_name} ${user.last_name}`;
    if (user?.first_name) return user.first_name;
    if (user?.last_name) return user.last_name;
    return user?.email || 'User';
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      clinical_site: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      university: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    };
    const labels = {
      admin: "Administrator",
      clinical_site: "Clinical Site Manager",
      university: "University Coordinator",
    };
    return <Badge className={styles[role]}>{labels[role]}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">Settings</h1>
        <p className="text-slate-400 mt-1 text-sm sm:text-base">
          Manage your account and notification preferences
        </p>
      </div>

      {/* Profile Card */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <User className="w-5 h-5 text-cyan-400" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-cyan-400/10 rounded-2xl flex items-center justify-center">
              <User className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-slate-100">{getUserDisplayName()}</h3>
              {getRoleBadge(user?.role)}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  First Name
                </Label>
                <Input
                  value={user?.first_name || ""}
                  disabled
                  className="bg-slate-800/50 border-slate-700 text-slate-300"
                  data-testid="profile-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Last Name
                </Label>
                <Input
                  value={user?.last_name || ""}
                  disabled
                  className="bg-slate-800/50 border-slate-700 text-slate-300"
                  data-testid="profile-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                value={user?.email || ""}
                disabled
                className="bg-slate-800/50 border-slate-700 text-slate-300"
                data-testid="profile-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400 text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organization
              </Label>
              <Input
                value={user?.organization || "Not specified"}
                disabled
                className="bg-slate-800/50 border-slate-700 text-slate-300"
                data-testid="profile-organization"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Role
              </Label>
              <Input
                value={user?.role === "admin" ? "Administrator" : user?.role === "clinical_site" ? "Clinical Site Manager" : "University Coordinator"}
                disabled
                className="bg-slate-800/50 border-slate-700 text-slate-300"
                data-testid="profile-role"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences Card */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingPrefs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Email Notifications */}
              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${notificationPrefs.email_notifications ? 'bg-cyan-400/10' : 'bg-slate-700/50'}`}>
                    <Mail className={`w-5 h-5 ${notificationPrefs.email_notifications ? 'text-cyan-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="text-slate-100 font-medium">Email Notifications</p>
                    <p className="text-slate-400 text-sm">
                      Receive placement updates, approvals, and alerts via email
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.email_notifications}
                  onCheckedChange={(checked) => updateNotificationPreference('email_notifications', checked)}
                  disabled={savingPrefs}
                  data-testid="email-notifications-toggle"
                />
              </div>

              {/* In-App Notifications */}
              <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${notificationPrefs.inapp_notifications ? 'bg-cyan-400/10' : 'bg-slate-700/50'}`}>
                    <Bell className={`w-5 h-5 ${notificationPrefs.inapp_notifications ? 'text-cyan-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="text-slate-100 font-medium">In-App Notifications</p>
                    <p className="text-slate-400 text-sm">
                      Show notification alerts within the application
                    </p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.inapp_notifications}
                  onCheckedChange={(checked) => updateNotificationPreference('inapp_notifications', checked)}
                  disabled={savingPrefs}
                  data-testid="inapp-notifications-toggle"
                />
              </div>

              {/* Student Email Notifications - Only for University users */}
              {user?.role === "university" && (
                <>
                  <div className="border-t border-slate-700 my-4" />
                  <div className="mb-2">
                    <p className="text-slate-300 font-medium flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-emerald-400" />
                      Student Notifications
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      Control email notifications sent to students in your university
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${notificationPrefs.student_email_notifications ? 'bg-emerald-400/10' : 'bg-slate-700/50'}`}>
                        <Users className={`w-5 h-5 ${notificationPrefs.student_email_notifications ? 'text-emerald-400' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <p className="text-slate-100 font-medium">Student Email Notifications</p>
                        <p className="text-slate-400 text-sm">
                          Send placement updates to students via email
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationPrefs.student_email_notifications}
                      onCheckedChange={(checked) => updateNotificationPreference('student_email_notifications', checked)}
                      disabled={savingPrefs}
                      className="data-[state=checked]:bg-emerald-500"
                      data-testid="student-email-notifications-toggle"
                    />
                  </div>
                </>
              )}

              <p className="text-slate-500 text-xs mt-2">
                Note: Critical system notifications may still be sent regardless of these settings.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Sign Out</h3>
              <p className="text-slate-400 text-sm mt-1">Log out of your account</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="destructive"
              className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
